import { useQuery } from '@tanstack/react-query';

import { hubFetch } from '@/api/client';
import type { IncidentPagedResult, IncidentRow, IncidentStateMode } from '@/api/types';
import type { HubEnv } from '@/env/types';
import { useEnvStore } from '@/env/envStore';
import { demoIncidents, isDemoModeRequested } from '@/lib/demoData';

export interface IncidentFilter {
  severity?: number;
  state?: IncidentStateMode;
  take?: number;
  continuation?: string;
}

function matchesStateMode(alertState: string, state: IncidentStateMode): boolean {
  switch (state) {
    case 'Active':
      return alertState === 'New' || alertState === 'Acknowledged';
    case 'All':
      return true;
    default:
      return alertState === state;
  }
}

export function filterIncidentItems(
  items: IncidentRow[],
  env: HubEnv,
  filter: IncidentFilter,
): IncidentRow[] {
  const state = filter.state ?? 'Active';
  return items.filter((row) => {
    if (row.env.toLowerCase() !== env) return false;
    if (filter.severity !== undefined && row.severity !== filter.severity) return false;
    return matchesStateMode(row.alertState, state);
  });
}

export function useIncidents(filter: IncidentFilter = {}) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  const state = filter.state ?? 'Active';
  const normalizedFilter = { ...filter, state };
  return useQuery({
    queryKey: ['incidents', env, normalizedFilter, demo],
    queryFn: (): Promise<IncidentPagedResult> => {
      if (demo) {
        const all = demoIncidents();
        return Promise.resolve({
          ...all,
          items: filterIncidentItems(all.items, env, normalizedFilter),
        });
      }
      return hubFetch<IncidentPagedResult>('/api/incidents', {
        searchParams: {
          severity: filter.severity,
          state,
          ...(filter.take !== undefined && { take: Math.min(200, Math.max(1, filter.take)) }),
          continuation: filter.continuation,
        },
      });
    },
    staleTime: 2 * 60_000,
    refetchInterval: 2 * 60_000,
  });
}
